import { FC, useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, InputNumber } from 'antd';
import { Clinic } from '../../../models/MedVox/Clinic';
import { CreateCallPoolRequest } from '../../../models/Settings/CallPool';
import { ClinicSelectionEditor } from '../../../components/CallPoolSettings/ClinicSelectionEditor/ClinicSelectionEditor';
import styles from './style.module.css';

const MIN_CHANNELS_COUNT = 1;
const MAX_CHANNELS_COUNT = 200;

const normalizeChannelsCount = (value: number | null) => {
  const numericValue = value ?? MIN_CHANNELS_COUNT;
  if (!Number.isFinite(numericValue)) return MIN_CHANNELS_COUNT;
  return Math.min(
    MAX_CHANNELS_COUNT,
    Math.max(MIN_CHANNELS_COUNT, Math.trunc(numericValue)),
  );
};

interface Props {
  open: boolean;
  saving: boolean;
  clinics: Clinic[];
  mode?: 'create' | 'edit';
  initialValues?: {
    readonly title: string;
    readonly channelsCount: number;
    readonly clinicIds: readonly string[];
  };
  onClose: () => void;
  onSubmit: (values: CreateCallPoolRequest) => void;
}

export const CreateCallPoolModal: FC<Props> = ({
  open,
  saving,
  clinics,
  mode = 'create',
  initialValues,
  onClose,
  onSubmit,
}) => {
  const [title, setTitle] = useState('');
  const [channelsCount, setChannelsCount] = useState(MIN_CHANNELS_COUNT);
  const [clinicIds, setClinicIds] = useState<string[]>([]);

  const selectedClinics = useMemo(
    () =>
      clinicIds
        .map((clinicId) => clinics.find((clinic) => clinic.id === clinicId))
        .filter((clinic): clinic is Clinic => Boolean(clinic)),
    [clinicIds, clinics],
  );
  const modalTitle = mode === 'edit' ? 'Редактировать пул' : 'Добавить пул';
  const submitText = mode === 'edit' ? 'Сохранить' : 'Создать';

  useEffect(() => {
    if (!open) {
      return;
    }

    setTitle(initialValues?.title ?? '');
    setChannelsCount(
      normalizeChannelsCount(
        initialValues?.channelsCount ?? MIN_CHANNELS_COUNT,
      ),
    );
    setClinicIds([...(initialValues?.clinicIds ?? [])]);
  }, [
    initialValues?.channelsCount,
    initialValues?.clinicIds,
    initialValues?.title,
    open,
  ]);

  const resetForm = () => {
    setTitle('');
    setChannelsCount(MIN_CHANNELS_COUNT);
    setClinicIds([]);
  };

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      channelsCount: normalizeChannelsCount(channelsCount),
      clinicIds,
    });
  };

  const handleAfterClose = () => {
    resetForm();
  };

  return (
    <Modal
      title={modalTitle}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      afterClose={handleAfterClose}
      okText={submitText}
      cancelText="Отмена"
      confirmLoading={saving}
      okButtonProps={{ disabled: !title.trim() }}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form layout="vertical">
        <div
          className={styles.mainFieldsRow}
          data-testid="callpool-main-fields-row"
        >
          <Form.Item
            className={styles.titleField}
            label="Наименование пула"
            required
          >
            <Input
              placeholder="Введите наименование"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={saving}
            />
          </Form.Item>
          <Form.Item className={styles.channelsField} label="Каналы">
            <InputNumber
              min={MIN_CHANNELS_COUNT}
              max={MAX_CHANNELS_COUNT}
              precision={0}
              value={channelsCount}
              onChange={(value) =>
                setChannelsCount(normalizeChannelsCount(value))
              }
              disabled={saving}
            />
          </Form.Item>
        </div>
        <Form.Item label="Медицинские организации">
          <ClinicSelectionEditor
            selectedClinics={selectedClinics}
            allClinics={clinics}
            onChange={(nextSelectedClinics) =>
              setClinicIds(nextSelectedClinics.map((clinic) => clinic.id))
            }
            disabled={saving}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

CreateCallPoolModal.displayName = 'CreateCallPoolModal';
